import React, { useEffect, useMemo, useState } from 'react';

function formatProjectLabel(project) {
  return `${project.name} (${project.jira_key})`;
}

export default function ProjectAutocomplete({
  projects,
  selectedProjectKey,
  onSelectProject,
}) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const selectedProject = useMemo(
    () => projects.find((project) => project.jira_key === selectedProjectKey) || null,
    [projects, selectedProjectKey]
  );

  useEffect(() => {
    if (!selectedProject) {
      setQuery('');
      return;
    }
    setQuery(formatProjectLabel(selectedProject));
  }, [selectedProject]);

  const filteredProjects = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return projects.slice(0, 12);

    return projects
      .filter((project) => {
        const client = project.client_name || '';
        const label = `${project.name} ${project.jira_key} ${client}`.toLowerCase();
        return label.includes(term);
      })
      .slice(0, 12);
  }, [projects, query]);

  const handleSelect = (projectKey) => {
    onSelectProject(projectKey);
    setIsOpen(false);
  };

  const handleInputChange = (event) => {
    const value = event.target.value;
    setQuery(value);
    setIsOpen(true);

    if (!value.trim()) {
      onSelectProject('');
      return;
    }

    if (selectedProject && value !== formatProjectLabel(selectedProject)) {
      onSelectProject('');
    }
  };

  const handleClear = () => {
    setQuery('');
    setIsOpen(false);
    onSelectProject('');
  };

  return (
    <div className="autocomplete">
      <div className="autocomplete-input-wrap">
        <input
          className="autocomplete-input"
          type="text"
          value={query}
          placeholder="Todos os projetos"
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setTimeout(() => setIsOpen(false), 120)}
        />
        {query && (
          <button
            type="button"
            className="autocomplete-clear"
            onMouseDown={(event) => event.preventDefault()}
            onClick={handleClear}
            aria-label="Limpar filtro"
          >
            Limpar
          </button>
        )}
      </div>

      {isOpen && (
        <div className="autocomplete-dropdown">
          <button
            type="button"
            className={`autocomplete-option ${selectedProjectKey === '' ? 'selected' : ''}`}
            onMouseDown={(event) => {
              event.preventDefault();
              handleSelect('');
            }}
          >
            <span>Todos os projetos</span>
            <span className="autocomplete-meta">remove o filtro</span>
          </button>

          {filteredProjects.map((project) => (
            <button
              type="button"
              key={project.jira_key}
              className={`autocomplete-option ${selectedProjectKey === project.jira_key ? 'selected' : ''}`}
              onMouseDown={(event) => {
                event.preventDefault();
                handleSelect(project.jira_key);
              }}
            >
              <span>{project.name}</span>
              <span className="autocomplete-meta">
                {project.jira_key}
                {project.client_name ? ` · ${project.client_name}` : ''}
              </span>
            </button>
          ))}

          {filteredProjects.length === 0 && (
            <div className="autocomplete-empty">Nenhum projeto encontrado.</div>
          )}
        </div>
      )}
    </div>
  );
}
